import c3 from "c3";

import { AxisConfiguration, ChartDataPoint } from "@/Patient/Models/GrowthCharts/ChartData";
import { C3Chart, C3ContentData } from "./C3Chart";

interface ILineChart {
    readonly id: string;
    readonly xAxis: AxisConfiguration;
    readonly yAxis: AxisConfiguration;
    readonly primary: ILineChartConfig;
    readonly secondary?: ILineChartConfig;
}

interface ILineChartConfig {
    label: string;
    dataPoints: ChartDataPoint[];
}

interface ILineChartGraphProps {
    chart: ILineChart;
    onDataPointHover?: (data: ChartDataPoint) => void;
    onDataPointClick?: (id: string) => void;
}

class LineChartGraph extends C3Chart<ILineChartGraphProps, never> {
    public shouldComponentUpdate(nextProps: ILineChartGraphProps): boolean {
        return this.props.chart !== nextProps.chart;
    }

    protected getC3Config(): c3.ChartConfiguration {
        const defaultConfig = super.getC3Config();
        defaultConfig.padding.right = 20;
        return defaultConfig;
    }

    protected getControlId(): string {
        return this.props.chart.id;
    }

    protected getData(): c3.Data {
        const xAxis1Name = "x1";
        const chart = this.props.chart;
        const xs = {
            [chart.primary.label]: xAxis1Name
        };

        const conlumns = [];
        conlumns.push(([xAxis1Name] as any[]).concat(chart.primary.dataPoints?.map(p => p.x) ?? []));
        conlumns.push(([chart.primary.label] as any[]).concat(chart.primary.dataPoints?.map(p => p.y) ?? []));

        if (chart.secondary) {
            const xAxis2Name = "x2";
            xs[chart.secondary.label] = xAxis2Name;
            conlumns.push(([xAxis2Name] as any[]).concat(chart.secondary.dataPoints?.map(p => p.x) ?? []));
            conlumns.push(([chart.secondary.label] as any[]).concat(chart.secondary.dataPoints.map(p => p.y)));
        }

        return {
            xs: xs,
            columns: conlumns,
            onmouseout: () => this.props.onDataPointHover?.(null),
            onclick: data => this.handleDataPointClick(data)
        };
    }

    private handleDataPointClick(data: C3ContentData): void {
        const pointData = this.getPointDataByValue(data);
        if (pointData?.medicalItemId && this.props.onDataPointClick)
            this.props.onDataPointClick(pointData.medicalItemId);
    }

    protected getAxis(): c3.Axis {
        return {
            x: this.getXAxisConfig(),
            y: this.getYAxisConfig()
        };
    }

    protected getTooltip(): c3.TooltipOptions {
        return {
            contents: (d: C3ContentData[]) => {
                if (d?.length)
                    this.handleOnMouseOver(d[0]);

                return "";
            }
        };
    }

    private getPointDataByValue(data: C3ContentData): ChartDataPoint {
        if (data.id === this.props.chart.primary.label)
            return this.props.chart.primary.dataPoints[data.index];
        else if (data.id === this.props.chart.secondary?.label)
            return this.props.chart.secondary.dataPoints[data.index];

        return null;
    }

    private handleOnMouseOver(data: C3ContentData): void {
        const pointData = this.getPointDataByValue(data);
        this.props.onDataPointHover?.(pointData);
    }

    private getXAxisConfig(): c3.XAxisConfiguration {
        const xAxis = this.props.chart.xAxis;
        if (!xAxis)
            return null;

        return {
            label: xAxis.label,
            type: "timeseries",
            tick: {
                format: "%Y-%m-%d",
                rotate: 75,
                multiline: false
            }
        };
    }

    private getYAxisConfig(): c3.YAxisConfiguration {
        const yAxis = this.props.chart.yAxis;
        if (!yAxis)
            return null;

        return {
            label: {
                text: yAxis.label,
                position: "outer-middle"
            },
            min: yAxis.min,
            max: yAxis.max
        };
    }
}

export { ILineChart, ILineChartConfig, LineChartGraph };
